import { useState } from "react";
import { motion } from "framer-motion";
import "@/App.css";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Progress } from "./components/ui/progress";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Github, Linkedin, Twitter, Mail, Sparkles, Zap, Heart, Code, Rocket, Star, Instagram, Award, Briefcase, Trophy } from "lucide-react";
import { toast } from "sonner";

function App() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const skills = [
    { name: "Python", level: 95, color: "--sky-blue", icon: "🐍" },
    { name: "Java & Spring", level: 90, color: "--mint-green", icon: "☕" },
    { name: "Kubernetes", level: 85, color: "--primary", icon: "⚓" },
    { name: "GCP", level: 85, color: "--coin-gold", icon: "☁️" },
    { name: "MongoDB", level: 88, color: "--mint-green", icon: "🍃" },
    { name: "PostgreSQL", level: 87, color: "--sky-blue", icon: "🐘" },
    { name: "Redis", level: 82, color: "--heart-red", icon: "⚡" },
    { name: "Camunda BPMN", level: 80, color: "--primary", icon: "🔄" },
    { name: "DSA", level: 92, color: "--coin-gold", icon: "🧩" },
    { name: "System Design", level: 90, color: "--sky-blue", icon: "🏗️" },
    { name: "Design Patterns", level: 88, color: "--mint-green", icon: "🎨" }
  ];

  const experiences = [
    {
      id: 1,
      company: "Wayfair",
      role: "Software Engineer",
      period: "Sept 2023 - Present",
      achievements: [
        "Engineered Facilities Management System supporting 250K facilities for optimal scalability",
        "Developed Lanes UI handling 4.5M lanes, optimizing performance and data management",
        "Created SyncGuard: no-code/low-code data reconciliation platform achieving 99.99% accuracy",
        "Automated Excel processing via File Upload API, processing 200K+ schedules monthly"
      ],
      logo: "/wayfair-logo.png"
    },
    {
      id: 2,
      company: "Falabella",
      role: "Lead Software Engineer",
      period: "Jan 2023 - Sept 2023",
      achievements: [
        "Led fulfillment orchestrator design handling 400K API requests and 4M events daily",
        "Designed Order 360: event-driven system processing 10M package updates per day"
      ],
      logo: "/tcs-logo.png"
    },
    {
      id: 3,
      company: "Falabella",
      role: "Senior Software Engineer",
      period: "Apr 2021 - Jan 2023",
      achievements: [
        "Built Digital Factory Logistics: 70% orders delivered <24hrs, 90% <48hrs",
        "Reduced Datadog costs by 30% through efficient log management optimization",
        "Implemented idempotent handlers for enterprise APIs ensuring reliability",
        "Created modular core starter kit standardizing microservices for 10+ teams"
      ],
      logo: "/tcs-logo.png"
    },
    {
      id: 4,
      company: "Tata Consultancy Services",
      role: "Systems Engineer",
      period: "Jun 2019 - Apr 2021",
      achievements: [
        "Developed promotional offer engine for automobile client with async reporting",
        "Built digital charts and focus vehicle charts for consolidated offer views",
        "Integrated auditing for offer change tracking adopted by 250+ operations teams"
      ],
      logo: "/falabella-logo.png"
    }
  ];

  const achievements = [
    {
      id: 1,
      title: "Change-Maker Award",
      organization: "Falabella",
      description: "Built solutions at scale like pick-and-dispatch and cross-docking capabilities",
      icon: "🏆",
      link: "https://www.linkedin.com/posts/falabella-india_changemakers-innovators-ideators-activity-6917018579594883072-AZIP"
    },
    {
      id: 2,
      title: "Winner - Technergize",
      organization: "IEEE CSIS 2018",
      description: "Built Breast Cancer Phase Detection Solution using Deep Learning (BreastX)",
      icon: "🥇",
      link: "https://tinyurl.com/zf2zxhcr"
    },
    {
      id: 3,
      title: "Best Final Year Project",
      organization: "2019",
      description: "Breast Cancer Histology Image Classification Using Deep Neural Networks",
      icon: "🎓",
      link: "https://tinyurl.com/yvdrm5sz"
    },
    {
      id: 4,
      title: "Scaler Coding Contest Winner",
      organization: "Scaler Academy",
      description: "Secured 2nd position in online coding competition",
      icon: "💻",
      link: "https://tinyurl.com/mrxumcxw"
    },
    {
      id: 5,
      title: "O-Star Performer",
      organization: "TCS",
      description: "Awarded for working on Contextual Chatbot for bank customer issue classification",
      icon: "⭐",
      link: "https://tinyurl.com/3d6hk2fd"
    },
    {
      id: 6,
      title: "LeetCode 1604 Rating",
      organization: "LeetCode",
      description: "Attained 1604 rating through weekly coding contests",
      icon: "🎮",
      link: "https://leetcode.com/giteshjain844"
    }
  ];

  const projects = [
    {
      id: 1,
      title: "Zeta",
      description: "Text intent Classification for Banking services",
      tags: ["NLP", "Python", "Keras"],
      details: "Automated user complaints handling using chatbot which classifies intent from text to resolve the issue on time. Built with Keras, Python, NLP, and Machine Learning.",
      link: "https://github.com/GiteshJ"
    },
    {
      id: 2,
      title: "BreastX",
      description: "Breast Cancer Phase Detection using Deep Learning",
      tags: ["ML", "CNN", "Python"],
      details: "Classification of Breast cancer histology images into four phases which helps in early detection of cancer. Built with Keras, Python, Machine Learning, CNN, and Deep Learning.",
      link: "https://github.com/GiteshJ"
    },
    {
      id: 3,
      title: "Banking System",
      description: "Miniature version of Banking Application",
      tags: ["Java", "SpringBoot", "PostgreSQL"],
      details: "Deposit and withdraw money, credit interest etc. with JWT based authentication. Built with Java, SpringBoot, and PostgreSQL.",
      link: "https://github.com/GiteshJ"
    },
    {
      id: 4,
      title: "mFlix",
      description: "Miniature version of Netflix",
      tags: ["Java", "SpringBoot", "MongoDB"],
      details: "Catalog for movies and shows. Built with Java, SpringBoot, and MongoDB for a seamless streaming experience.",
      link: "https://github.com/GiteshJ"
    },
    {
      id: 5,
      title: "GetPlaced",
      description: "Campus placement portal",
      tags: ["C#", "JavaScript", "MySQL"],
      details: "Takes care of all steps from candidate registration, online test to getting hired. Built with C#, JavaScript, and MySQL.",
      link: "https://github.com/GiteshJ"
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: "Saransh Agarwal",
      role: "Software Engineer @ BMO",
      text: "Gitesh has the capability to design complicated workflows with ease. He has a progressive approach to solving problems. He's always ready to take challenges even when time is short. His constructive attitude towards everything he does makes him an analytical thinker. A must have asset for any team.",
      avatar: "/saransh-agarwal.jpeg"
    },
    {
      id: 2,
      name: "Mohit Kumar",
      role: "Senior Analyst @ Everest Group",
      text: "Gitesh is a diligent individual and it had been great working with him. His calm in critical situations and his amicable nature benefitted not only the project, but also the team culture and other team members. All the best for your future endeavours.",
      avatar: "/mohit-kumar.jpeg"
    },
    {
      id: 3,
      name: "Ankur Sharma",
      role: "Senior MTS @ Oracle Cloud",
      text: "Gitesh is very proficient in Backend development with a deep understanding of competitive programming. He is collaborative, decisive, and excels in communication and interpersonal skills. Gitesh demonstrates mastery in Data Structures and Problem-solving. What truly stands out about Gitesh is his unwavering dedication to continuous learning and self-improvement.",
      avatar: "/ankur-sharma.jpeg"
    },
    {
      id: 4,
      name: "Amit Kumar Sah",
      role: "Product Owner @ Falabella India",
      text: "Gitesh is an outstanding engineer. In addition to his exceptional coding abilities, his logical thinking and positive attitude distinguish him from others in his field. He demonstrates a strong grasp of the fundamentals and exhibits dedication and leadership qualities that are sure to propel him far in his career.",
      avatar: "/amit-sah.jpeg"
    }
  ];

  const handleCoinCollect = () => {
    setCoins(coins + 1);
    toast.success("+1 Coin Collected!", {
      duration: 2000,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        toast.success("Message sent! Thanks for reaching out! 🎮", {
          duration: 3000,
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        toast.error("Failed to send message. Please try again.", {
          duration: 3000,
        });
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.", {
        duration: 3000,
      });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] font-body">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="sticky top-0 z-50 bg-white border-b-[3px] border-[color:hsl(var(--pixel-outline))] shadow-md"
        data-testid="header"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 border-[3px] border-[color:hsl(var(--pixel-outline))] overflow-hidden"
                data-testid="logo"
              >
                <img src="/profile-photo.png" alt="Gitesh Jain" className="w-full h-full object-cover" />
              </motion.div>
              <span className="font-pixel text-lg font-bold" data-testid="logo-text">GITESH</span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
              <button
                onClick={() => scrollToSection('about')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-about"
              >
                ABOUT
              </button>
              <button
                onClick={() => scrollToSection('skills')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-skills"
              >
                SKILLS
              </button>
              <button
                onClick={() => scrollToSection('experience')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-experience"
              >
                EXPERIENCE
              </button>
              <button
                onClick={() => scrollToSection('projects')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-projects"
              >
                PROJECTS
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-contact"
              >
                CONTACT
              </button>
            </nav>

            {/* LinkedIn Link */}
            <motion.a
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              href="https://www.linkedin.com/in/gitesh1996"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-[hsl(var(--primary))] border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] hover:shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] transition-shadow"
              data-testid="linkedin-link"
              aria-label="LinkedIn Profile"
            >
              <Linkedin className="w-5 h-5 text-white" />
            </motion.a>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section
        id="about"
        className="relative py-12 sm:py-16 lg:py-24 overflow-hidden"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 pixel-grid opacity-50" />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, hsl(197 92% 96%) 0%, hsl(160 64% 96%) 55%, hsl(48 100% 96%) 100%)'
          }}
        />
        <div className="relative mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Avatar */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
              className="flex justify-center"
              data-testid="hero-avatar"
            >
              <div className="relative">
                <div className="w-64 h-64 border-[4px] border-[color:hsl(var(--pixel-outline))] shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] overflow-hidden animate-float">
                  <img src="/profile-photo.png" alt="Gitesh Jain" className="w-full h-full object-cover" />
                </div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-4 -right-4"
                >
                  <Star className="w-12 h-12 text-[hsl(var(--coin-gold))] fill-[hsl(var(--coin-gold))]" />
                </motion.div>
              </div>
            </motion.div>

            {/* Hero Content */}
            <div className="space-y-6" data-testid="hero-content">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className="font-pixel text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" data-testid="hero-title">
                  GITESH JAIN
                </h1>
              </motion.div>
              
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-press-start text-base sm:text-lg text-[hsl(var(--primary))] tracking-wide" data-testid="hero-subtitle">
                  SOFTWARE ENGINEER
                </h2>
              </motion.div>

              <motion.p
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-base sm:text-base leading-relaxed text-[hsl(var(--muted-foreground))]"
                data-testid="hero-description"
              >
                Software Engineer with ~7 years of experience building scalable, high-impact systems in e-commerce and logistics. 
                Currently at Wayfair, designing large-scale platforms supporting millions of events and high-volume data processing. 
                Strong in Java, Spring Boot, distributed systems, and cloud (GCP/Azure), with a focus on reliability, performance, and clean architecture. 
                Passionate about solving complex system design problems and building systems that scale.
              </motion.p>

              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4"
                data-testid="hero-cta-container"
              >
                <Button
                  onClick={() => scrollToSection('projects')}
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] active:shadow-[0_0_0_0_hsl(var(--pixel-outline))] active:translate-x-[4px] active:translate-y-[4px] bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--sky-blue))] font-press-start text-xs px-6 py-6"
                  data-testid="hero-primary-cta"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  VIEW PROJECTS
                </Button>
                <Button
                  onClick={() => scrollToSection('contact')}
                  variant="outline"
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] active:shadow-[0_0_0_0_hsl(var(--pixel-outline))] active:translate-x-[4px] active:translate-y-[4px] bg-white text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] font-press-start text-xs px-6 py-6"
                  data-testid="hero-secondary-cta"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  HIRE ME
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section
        id="skills"
        className="py-12 sm:py-16 lg:py-24 bg-white"
        data-testid="skills-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="skills-title">
              PLAYER STATS
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="skills-subtitle">
              Skills leveled up through countless side quests
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="skills-grid">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                data-testid={`skill-item-${index}`}
              >
                <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-white shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-pixel text-base" data-testid={`skill-name-${index}`}>{skill.name}</h3>
                    <span className="text-2xl">{skill.icon}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-press-start text-xs text-[hsl(var(--muted-foreground))]">LVL</span>
                      <span className="font-press-start text-xs" data-testid={`skill-level-${index}`}>{skill.level}</span>
                    </div>
                    <div
                      className="h-6 border-[3px] rounded-none border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--muted))] overflow-hidden"
                      data-testid={`skill-stat-bar-${index}`}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
                        className="h-full"
                        style={{ backgroundColor: `hsl(var(${skill.color}))` }}
                      />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section
        id="experience"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--muted))/30]"
        data-testid="experience-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="experience-title">
              CAREER JOURNEY
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="experience-subtitle">
              Professional quests completed across the tech realm
            </p>
          </motion.div>

          <div className="space-y-6" data-testid="experience-list">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ x: index % 2 === 0 ? -100 : 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`experience-item-${index}`}
              >
                <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-white shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 flex items-center justify-center" data-testid={`experience-logo-${index}`}>
                      <img src={exp.logo} alt={`${exp.company} logo`} className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-pixel text-xl mb-1" data-testid={`experience-company-${index}`}>{exp.company}</h3>
                          <p className="font-press-start text-xs text-[hsl(var(--primary))]" data-testid={`experience-role-${index}`}>{exp.role}</p>
                        </div>
                        <Badge className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--coin-gold))] text-[hsl(var(--foreground))] font-press-start text-xs px-3 py-1">
                          {exp.period}
                        </Badge>
                      </div>
                      <ul className="space-y-2">
                        {exp.achievements.map((achievement, achIndex) => (
                          <li key={achIndex} className="flex items-start gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid={`experience-achievement-${index}-${achIndex}`}>
                            <span className="text-[hsl(var(--primary))] mt-1">▸</span>
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section
        id="achievements"
        className="py-12 sm:py-16 lg:py-24 bg-white"
        data-testid="achievements-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="achievements-title">
              TROPHY ROOM
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="achievements-subtitle">
              Legendary achievements unlocked
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="achievements-grid">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.id}
                initial={{ scale: 0, rotate: -10 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4, rotate: 2 }}
                data-testid={`achievement-card-${index}`}
              >
                <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-white shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-all cursor-pointer h-full">
                  <div className="p-6 space-y-3 text-center">
                    <div className="text-5xl mb-3" data-testid={`achievement-icon-${index}`}>{achievement.icon}</div>
                    <h3 className="font-pixel text-base" data-testid={`achievement-title-${index}`}>{achievement.title}</h3>
                    <p className="font-press-start text-xs text-[hsl(var(--primary))]" data-testid={`achievement-org-${index}`}>{achievement.organization}</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid={`achievement-desc-${index}`}>
                      {achievement.description}
                    </p>
                    {achievement.link && (
                      <a
                        href={achievement.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 font-press-start text-xs text-[hsl(var(--primary))] hover:text-[hsl(var(--sky-blue))] transition-colors"
                        data-testid={`achievement-link-${index}`}
                      >
                        VIEW →
                      </a>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section
        id="projects"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--muted))/30]"
        data-testid="projects-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="projects-title">
              QUEST LOG
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="projects-subtitle">
              Epic projects completed on the journey
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                data-testid={`project-card-${index}`}
              >
                <Card
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-white shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow cursor-pointer h-full"
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="p-4 space-y-3">
                    <h3 className="font-pixel text-lg" data-testid={`project-title-${index}`}>{project.title}</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid={`project-description-${index}`}>
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, tagIndex) => (
                        <Badge
                          key={tag}
                          className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--primary))] hover:text-white font-press-start text-xs px-2 py-1"
                          data-testid={`project-tag-${index}-${tagIndex}`}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        id="testimonials"
        className="py-12 sm:py-16 lg:py-24 bg-white"
        data-testid="testimonials-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="testimonials-title">
              PARTY MEMBERS
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="testimonials-subtitle">
              What my teammates say about our adventures
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6" data-testid="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`testimonial-card-${index}`}
              >
                <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-white shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow p-6 h-full">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-[3px] border-[color:hsl(var(--pixel-outline))]" data-testid={`testimonial-avatar-${index}`}>
                      <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-pixel text-base mb-1" data-testid={`testimonial-name-${index}`}>{testimonial.name}</h3>
                      <p className="font-press-start text-xs text-[hsl(var(--primary))] mb-3" data-testid={`testimonial-role-${index}`}>{testimonial.role}</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] italic" data-testid={`testimonial-text-${index}`}>
                        "{testimonial.text}"
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-12 sm:py-16 lg:py-24 bg-white"
        data-testid="contact-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-3xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="contact-title">
              START A QUEST
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="contact-subtitle">
              Let's team up and build something legendary
            </p>
          </motion.div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
          >
            <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-white shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="font-press-start text-xs block mb-2" htmlFor="name">
                    NAME
                  </label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] focus:ring-2 focus:ring-[hsl(var(--primary))] font-body"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="font-press-start text-xs block mb-2" htmlFor="email">
                    EMAIL
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] focus:ring-2 focus:ring-[hsl(var(--primary))] font-body"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="font-press-start text-xs block mb-2" htmlFor="message">
                    MESSAGE
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell me about your project..."
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] focus:ring-2 focus:ring-[hsl(var(--primary))] min-h-[120px] font-body"
                    required
                    data-testid="contact-message-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] active:shadow-[0_0_0_0_hsl(var(--pixel-outline))] active:translate-x-[4px] active:translate-y-[4px] bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--sky-blue))] font-press-start text-xs py-6"
                  data-testid="contact-submit-button"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  SEND MESSAGE
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="bg-[hsl(var(--pixel-outline))] text-white py-12"
        data-testid="footer"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* About */}
            <div data-testid="footer-about">
              <h3 className="font-pixel text-xl mb-4">GITESH JAIN</h3>
              <p className="text-sm text-gray-300">
                Software Engineer crafting reliable and scalable systems. Always ready for the next challenge.
              </p>
            </div>

            {/* Quick Links */}
            <div data-testid="footer-links">
              <h3 className="font-pixel text-xl mb-4">QUICK LINKS</h3>
              <div className="space-y-2">
                <button
                  onClick={() => scrollToSection('about')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-about"
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection('skills')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-skills"
                >
                  Skills
                </button>
                <button
                  onClick={() => scrollToSection('projects')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-projects"
                >
                  Projects
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-contact"
                >
                  Contact
                </button>
              </div>
            </div>

            {/* Social */}
            <div data-testid="footer-social">
              <h3 className="font-pixel text-xl mb-4">CONNECT</h3>
              <div className="flex gap-4">
                <motion.a
                  whileHover={{ y: -4 }}
                  href="https://github.com/GiteshJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white border-[3px] border-white flex items-center justify-center hover:bg-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-social-github"
                >
                  <Github className="w-6 h-6 text-[hsl(var(--pixel-outline))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4 }}
                  href="https://www.linkedin.com/in/gitesh1996"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white border-[3px] border-white flex items-center justify-center hover:bg-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-social-linkedin"
                >
                  <Linkedin className="w-6 h-6 text-[hsl(var(--pixel-outline))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4 }}
                  href="https://www.instagram.com/_gitesh.jain_"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white border-[3px] border-white flex items-center justify-center hover:bg-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-social-instagram"
                >
                  <Instagram className="w-6 h-6 text-[hsl(var(--pixel-outline))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4 }}
                  href="https://leetcode.com/giteshjain844"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white border-[3px] border-white flex items-center justify-center hover:bg-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-social-leetcode"
                >
                  <Code className="w-6 h-6 text-[hsl(var(--pixel-outline))]" />
                </motion.a>
              </div>
            </div>
          </div>

          <div className="border-t-[3px] border-white/20 pt-8 text-center">
            <p className="font-press-start text-xs text-gray-300" data-testid="footer-copyright">
              © 2026 GITESH JAIN. BUILT WITH <Heart className="inline w-3 h-3 text-[hsl(var(--heart-red))] fill-[hsl(var(--heart-red))]" /> & TEA
            </p>
          </div>
        </div>
      </footer>

      {/* Project Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] max-w-2xl" data-testid="project-dialog">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle className="font-pixel text-2xl" data-testid="project-dialog-title">
                  {selectedProject.title}
                </DialogTitle>
                <DialogDescription className="text-base" data-testid="project-dialog-description">
                  {selectedProject.details}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map((tag) => (
                    <Badge
                      key={tag}
                      className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--primary))] text-white font-press-start text-xs px-3 py-1"
                      data-testid="project-dialog-tag"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-4">
                  <Button
                    asChild
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--sky-blue))] font-press-start text-xs"
                    data-testid="project-dialog-github-btn"
                  >
                    <a href="https://github.com/GiteshJ" target="_blank" rel="noopener noreferrer">
                      <Github className="w-4 h-4 mr-2" />
                      VIEW GITHUB
                    </a>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
